import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Container, Paper, Typography, Button } from '@mui/material'
import { getConfiguration, getSupersetConfiguration, getDomainNameDomainTypeMap, SupersetConfigItem, sendUpdateRequest, createNewConfig ,} from './editConfiguraitonService';
import DomainSelector from './components/DomainSelector'
import ConfigFormBuilder from './components/ConfigFormBuilder'
import { MetaDataConfigObject } from '../../types';
import { Loader } from '../loader/Loader';
import JsonDiffViewer from './components/JsonDiffViewer';
import { openPopup } from '../../stateManagement/actions/popupActions';


const EditConfiguration = () => {
    const [lobConfiguration, setLobConfiguration] = useState<MetaDataConfigObject[]>([]);
    const [supersetConfiguration, setSupersetConfiguration] = useState<SupersetConfigItem[]>([]);
    const [selectedDomain, setSelectedDomain] = useState<string | undefined>()
    const [selectedDomainType, setSelectedDomainType] = useState<string | undefined>()
    const [formData, setFormData] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [showJsonDiffViewer, setShowJsonDiffViewer] = useState(false)
    const [updatedLobConfiguration, setUpdatedLobConfiguration] = useState<any>([])
    const [initialLobConfiguration, setInitialLobConfiguration] = useState<any>([])
    const [errors, setErrors] = useState<any>([])
   
    const init = async () => {
        try{
            // get all the configruatoin for the lob
            setIsLoading(true)
            const response = await getConfiguration();
            const lobConfiguration = (response?.data?.features ?? []).map((item: any) => {
                return {
                    domainName: item.domainName,
                    domainType: item.domainType,
                    domainValues: item.domainValues
                }
            });
            setLobConfiguration(lobConfiguration);
            // get all the configruraion from the superset api 
            const supersetConfiguration=await getSupersetConfiguration()
            setSupersetConfiguration(supersetConfiguration);
        }catch{
            console.error("error in init");
        }finally{
            setIsLoading(false)
        }
    }

    const handleSave = () => {
        if(!selectedDomain || !selectedDomainType ) return;
        const validInput = validateInput(formData);
        if(!validInput){
            openPopup("Error","Duplicate values are not allowed");
            return;
        }
        const initialLobConfiguration = lobConfiguration.find((item) => item.domainName === selectedDomain && item.domainType === selectedDomainType)?.domainValues ?? [];
        setUpdatedLobConfiguration(formData);
        setInitialLobConfiguration(initialLobConfiguration);
        setShowJsonDiffViewer(true);
    }
const validateInput = (formData: any) => {
    if(Array.isArray(formData)) {
        for(let i = 0 ; i<formData.length;i++){
            for(let j = i+1;j<formData.length;j++){
                if(deepEqual(formData[i],formData[j])){
                    return false;
                }
            }
        }
        return true;
    }
    else{
        return true;
    }
}
function deepEqual(a?: any, b?: any): boolean {
    // Handle strict equality (including same reference, primitives, null, undefined)
    if (a === b) return true;
    
    // Handle null/undefined cases
    if (a === null || a === undefined || b === null || b === undefined) {
      return a === b; // Both must be null/undefined to be equal
    }
    
    // Handle NaN - both must be NaN to be equal
    if (Number.isNaN(a) && Number.isNaN(b)) return true;
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    
    // Handle type mismatch
    if (typeof a !== typeof b) return false;
    
    // Handle arrays
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!deepEqual(a[i], b[i])) return false;
      }
      return true;
    }
    
    // If one is array and other isn't, they're not equal
    if (Array.isArray(a) || Array.isArray(b)) return false;
    
    // Handle objects (including nested objects)
    if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
      const aKeys = Object.keys(a);
      const bKeys = Object.keys(b);
      if (aKeys.length !== bKeys.length) return false;
      for (const k of aKeys) {
        if (!bKeys.includes(k)) return false;
        if (!deepEqual(a[k], (b as Record<string, any>)[k])) return false;
      }
      return true;
    }
    
    // For all other cases (primitives that didn't match in === check), they're not equal
    return false;
  }
const saveConfigiration = () => {
    setShowJsonDiffViewer(false);
    if(!selectedDomain || !selectedDomainType) return;
    const isCreationRequest = lobConfiguration.find((item) => item.domainName === selectedDomain && item.domainType === selectedDomainType) ? false : true;
    if(isCreationRequest){
        createNewConfig(selectedDomain,selectedDomainType,formData,init);
    }else{
        sendUpdateRequest(selectedDomain,selectedDomainType,formData,init);
    }
    
}

useEffect(()=>{
    init();
},[])

useEffect(()=>{
    // Reset all form-related state whenever domain or domain type changes
    setFormData(null);
    setErrors([]);
    setUpdatedLobConfiguration([]);
    setInitialLobConfiguration([]);
    setShowJsonDiffViewer(false);

    // Repopulate with the new domain/type values if both are selected
    if(selectedDomain && selectedDomainType){
        const lobInfo = lobConfiguration.find(
            (item) => item.domainName === selectedDomain && item.domainType === selectedDomainType
        );
        setFormData(lobInfo ? lobInfo.domainValues : null);
    }
},[selectedDomainType, selectedDomain, lobConfiguration])
    const domainTypeMap = useMemo(() => getDomainNameDomainTypeMap(supersetConfiguration ),[supersetConfiguration])
    const domains = useMemo(() => Object.keys(domainTypeMap), [domainTypeMap])
    const allowSave = useMemo(() => {
        if(!selectedDomain || !selectedDomainType) return false;
        return JSON.stringify(formData) !== JSON.stringify(lobConfiguration.find((item) => item.domainName === selectedDomain && item.domainType === selectedDomainType)?.domainValues ?? []) 
    }, [formData, lobConfiguration, selectedDomain, selectedDomainType])
  return isLoading? <Loader /> : (
    <Box sx={{  overflow: 'hidden', position: 'relative' }}>
        <Container maxWidth="xl" sx={{ py: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Superset Config Portal</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <DomainSelector
                    supersetConfiguration={supersetConfiguration}
                    domains={domains}
                    domainTypesByDomain={domainTypeMap}
                    selectedDomain={selectedDomain}
                    selectedDomainType={selectedDomainType}
                    onDomainChange={(d) => { setSelectedDomain(d); setSelectedDomainType(undefined);  }}
                    onDomainTypeChange={(t) => { setSelectedDomainType(t);  }}
                    formData={formData}
                />
            </Paper>
            {selectedDomainType!="" && selectedDomain!="" && (<Paper variant="outlined" sx={{ p: 2, height: '100%',width: '100%', overflow: 'auto' }}>
                    <ConfigFormBuilder
                        setErrors={setErrors}
                        formData={formData}
                        setFormData={setFormData}
                        lobConfiguration={lobConfiguration}
                        supersetConfiguration={supersetConfiguration}
                        selectedDomain={selectedDomain ?? ''}
                        selectedDomainType={selectedDomainType ?? ''}
                    />
                </Paper>)}
        </Container>

        {/* Sticky Save Button on the right */}
        <Box sx={{ position: 'fixed', top: '15%', right: 30, transform: 'translateY(-50%)' }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={!allowSave}>
                Save
            </Button>
        </Box>
        {showJsonDiffViewer && (
        <JsonDiffViewer
            initialJson={initialLobConfiguration}
            updatedJson={updatedLobConfiguration}
            onConfirm={saveConfigiration}
            onClose={() => setShowJsonDiffViewer(false)}
        />
        )}
    </Box>
  )
}

export default EditConfiguration

